import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { FactorDetailModal } from '../components/FactorDetailModal';
import { api } from '../lib/api';
import { useDebouncedEffect } from '../lib/hooks';
import { useFactors } from '../lib/factors';
import type { Factor, Level } from '@shared/factors';
import type { Client, Diagnostic as DiagnosticT, FactorScore } from '@shared/models';

interface DiagnosticDetail {
  diagnostic: DiagnosticT;
  client: Client;
  scores: FactorScore[];
}

interface ScoreState {
  level: Level | null;
  memo: string;
  dirty: boolean;
}

const RANK_LABEL: Record<Level, string> = { 5: '最良', 4: '良好', 3: '標準', 2: '要改善', 1: '危機' };
const RANK_BG: Record<Level, string> = {
  5: 'bg-rank-5 text-white',
  4: 'bg-rank-4 text-white',
  3: 'bg-rank-3 text-white',
  2: 'bg-rank-2 text-white',
  1: 'bg-rank-1 text-white',
};
const RANK_BG_LIGHT: Record<Level, string> = {
  5: 'bg-rank-5/10 text-rank-5 border-rank-5/40',
  4: 'bg-rank-4/10 text-rank-4 border-rank-4/40',
  3: 'bg-rank-3/10 text-rank-3 border-rank-3/40',
  2: 'bg-rank-2/10 text-rank-2 border-rank-2/40',
  1: 'bg-rank-1/10 text-rank-1 border-rank-1/40',
};

export default function Diagnostic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: factorsData } = useFactors();
  const [detail, setDetail] = useState<DiagnosticDetail | null>(null);
  const [scoresState, setScoresState] = useState<Record<number, ScoreState>>({});
  const [activeLayer, setActiveLayer] = useState<'I' | 'II' | 'III' | 'IV'>('I');
  const [savingState, setSavingState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [modalFactor, setModalFactor] = useState<Factor | null>(null);
  const [pendingChanges, setPendingChanges] = useState(0);

  // Initial load
  useEffect(() => {
    if (!id) return;
    api
      .get<DiagnosticDetail>(`/api/diagnostics/${id}`)
      .then((d) => {
        setDetail(d);
        const map: Record<number, ScoreState> = {};
        for (const s of d.scores) {
          map[s.factor_id] = { level: s.level, memo: s.memo ?? '', dirty: false };
        }
        setScoresState(map);
      })
      .catch((e) => {
        alert(e instanceof Error ? e.message : '読み込みに失敗しました');
      });
  }, [id]);

  // Debounced autosave (500ms after last change)
  useDebouncedEffect(
    async () => {
      const dirty = Object.entries(scoresState).filter(([, s]) => s.dirty);
      if (dirty.length === 0) return;
      setSavingState('saving');
      try {
        await api.put(`/api/diagnostics/${id}/scores`, {
          scores: dirty.map(([fid, s]) => ({
            factor_id: Number(fid),
            level: s.level,
            memo: s.memo || null,
          })),
        });
        setScoresState((prev) => {
          const next = { ...prev };
          for (const [fid] of dirty) next[Number(fid)] = { ...next[Number(fid)], dirty: false };
          return next;
        });
        setSavingState('saved');
        setTimeout(() => setSavingState((s) => (s === 'saved' ? 'idle' : s)), 1500);
      } catch (e) {
        setSavingState('error');
        console.error(e);
      }
    },
    pendingChanges,
    500,
  );

  const factorsByLayer = useMemo(() => {
    if (!factorsData) return {} as Record<string, Factor[]>;
    const grouped: Record<string, Factor[]> = {};
    for (const f of factorsData.factors) {
      grouped[f.layer] ||= [];
      grouped[f.layer].push(f);
    }
    return grouped;
  }, [factorsData]);

  const factorsByCategoryInLayer = useMemo(() => {
    const list = factorsByLayer[activeLayer] || [];
    const grouped: Record<string, { name: string; factors: Factor[] }> = {};
    for (const f of list) {
      grouped[f.category] ||= { name: f.category_name, factors: [] };
      grouped[f.category].factors.push(f);
    }
    return grouped;
  }, [factorsByLayer, activeLayer]);

  const totalCount = factorsData?.factors.length ?? 0;
  const filledCount = Object.values(scoresState).filter((s) => s.level != null).length;

  const setLevel = (factorId: number, level: Level) => {
    setScoresState((prev) => ({
      ...prev,
      [factorId]: {
        level: prev[factorId]?.level === level ? null : level,
        memo: prev[factorId]?.memo ?? '',
        dirty: true,
      },
    }));
    setPendingChanges((n) => n + 1);
  };

  const setMemo = (factorId: number, memo: string) => {
    setScoresState((prev) => ({
      ...prev,
      [factorId]: { level: prev[factorId]?.level ?? null, memo, dirty: true },
    }));
    setPendingChanges((n) => n + 1);
  };

  if (!detail || !factorsData) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="mx-auto max-w-6xl px-4 py-8 text-slate-400">読み込み中...</main>
      </div>
    );
  }

  const layerOrder: Array<'I' | 'II' | 'III' | 'IV'> = ['I', 'II', 'III', 'IV'];
  const currentLayerIdx = layerOrder.indexOf(activeLayer);

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{detail.client.name}</h1>
            <p className="text-sm text-slate-500">診断日: {detail.diagnostic.diagnostic_date}</p>
          </div>
          <div className="flex items-center gap-3">
            <SaveIndicator state={savingState} />
            <button
              onClick={() => navigate(`/diagnostics/${id}/report`)}
              className="btn-primary"
            >
              レポートを見る
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
            <span>全体進捗</span>
            <span>
              {filledCount} / {totalCount} 因子
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full bg-brand-accent transition-all"
              style={{ width: totalCount ? `${(filledCount / totalCount) * 100}%` : '0%' }}
            />
          </div>
        </div>

        {/* Layer tabs */}
        <div className="mb-6 flex gap-1 border-b border-slate-200">
          {factorsData.layers.map((l) => {
            const layerFactors = factorsByLayer[l.id] || [];
            const layerFilled = layerFactors.filter(
              (f) => scoresState[f.id]?.level != null,
            ).length;
            return (
              <button
                key={l.id}
                onClick={() => setActiveLayer(l.id as 'I' | 'II' | 'III' | 'IV')}
                className={`px-4 py-2 text-sm font-medium transition ${
                  activeLayer === l.id
                    ? 'border-b-2 border-brand text-brand'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {l.id} {l.name}
                <span className="ml-2 text-xs text-slate-400">
                  ({layerFilled}/{layerFactors.length})
                </span>
              </button>
            );
          })}
        </div>

        <div className="space-y-8">
          {Object.entries(factorsByCategoryInLayer).map(([catId, cat]) => (
            <section key={catId}>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                {catId} {cat.name}
              </h2>
              <div className="space-y-3">
                {cat.factors.map((f) => {
                  const s = scoresState[f.id];
                  return (
                    <div key={f.id} className="card">
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold text-slate-800">
                            <span className="mr-2 text-xs text-slate-400">#{f.id}</span>
                            {f.name}
                          </h3>
                          <p className="mt-1 text-xs text-slate-500">{f.method}</p>
                        </div>
                        <button
                          onClick={() => setModalFactor(f)}
                          className="shrink-0 text-xs text-brand-accent hover:underline"
                        >
                          詳細を見る
                        </button>
                      </div>

                      <div className="mb-3 grid grid-cols-5 gap-1">
                        {([1, 2, 3, 4, 5] as Level[]).map((lv) => {
                          const active = s?.level === lv;
                          return (
                            <button
                              key={lv}
                              onClick={() => setLevel(f.id, lv)}
                              className={`rounded-md border-2 px-2 py-2 text-xs font-medium transition ${
                                active ? RANK_BG[lv] : `${RANK_BG_LIGHT[lv]} hover:opacity-80`
                              }`}
                            >
                              <div className="font-bold">Lv.{lv}</div>
                              <div>{RANK_LABEL[lv]}</div>
                            </button>
                          );
                        })}
                      </div>

                      <input
                        type="text"
                        placeholder="メモ(任意)"
                        value={s?.memo ?? ''}
                        onChange={(e) => setMemo(f.id, e.target.value)}
                        className="input text-sm"
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-8 flex justify-between">
          <button
            disabled={currentLayerIdx === 0}
            onClick={() => setActiveLayer(layerOrder[Math.max(0, currentLayerIdx - 1)])}
            className="btn-secondary"
          >
            ← 前のレイヤー
          </button>
          <Link to="/" className="btn-secondary">
            ダッシュボードに戻る
          </Link>
          <button
            disabled={currentLayerIdx === layerOrder.length - 1}
            onClick={() =>
              setActiveLayer(layerOrder[Math.min(layerOrder.length - 1, currentLayerIdx + 1)])
            }
            className="btn-secondary"
          >
            次のレイヤー →
          </button>
        </div>
      </main>

      {modalFactor && (
        <FactorDetailModal factor={modalFactor} onClose={() => setModalFactor(null)} />
      )}
    </div>
  );
}

function SaveIndicator({ state }: { state: 'idle' | 'saving' | 'saved' | 'error' }) {
  const map = {
    idle: { text: '', color: 'text-slate-400' },
    saving: { text: '保存中...', color: 'text-slate-500' },
    saved: { text: '✓ 保存しました', color: 'text-rank-5' },
    error: { text: '保存に失敗しました', color: 'text-rank-1' },
  };
  const v = map[state];
  return <span className={`text-xs ${v.color}`}>{v.text}</span>;
}
